import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BookOpen, Search, ScrollText, Library, Compass, User, LogOut, Menu, X } from "lucide-react";

const nav = [
  { to: "/word", label: "Word Search", icon: Search, testid: "nav-word" },
  { to: "/verse", label: "Verse Lookup", icon: BookOpen, testid: "nav-verse" },
  { to: "/dictionary", label: "Vine's Dictionary", icon: ScrollText, testid: "nav-dictionary" },
  { to: "/topical", label: "Nave's Topical", icon: Compass, testid: "nav-topical" },
  { to: "/studies", label: "My Studies", icon: Library, testid: "nav-studies" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen parchment">
      {/* Top ribbon */}
      <header className="border-b hairline bg-[#FDFBF7] sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
            <div className="w-8 h-8 border border-[#9A3B3B] flex items-center justify-center">
              <span className="font-serif-display text-[#9A3B3B] text-xl leading-none">א</span>
            </div>
            <div className="leading-tight">
              <div className="font-serif-display text-xl text-stone-900">Logos Study</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500">Bible Word Study</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors ${
                    isActive ? "text-[#9A3B3B]" : "text-stone-700 hover:text-stone-900"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                <span className="text-sm text-stone-600" data-testid="user-email">{user.email}</span>
                <button
                  onClick={() => { logout(); navigate("/"); }}
                  data-testid="logout-btn"
                  className="text-sm text-stone-700 hover:text-[#9A3B3B] flex items-center gap-1"
                >
                  <LogOut size={14} strokeWidth={1.5} /> Sign out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="login-link" className="text-sm text-stone-700 hover:text-[#9A3B3B]">Sign in</Link>
                <Link to="/register" data-testid="register-link" className="text-sm bg-[#9A3B3B] hover:bg-[#7F2F2F] text-white px-4 py-2 transition-colors">Create account</Link>
              </>
            )}
          </div>

          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="md:hidden border-t hairline bg-[#FDFBF7]">
            <div className="px-6 py-4 flex flex-col gap-3">
              {nav.map((n) => (
                <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
                  className={({ isActive }) => `text-sm ${isActive ? "text-[#9A3B3B]" : "text-stone-700"}`}
                  data-testid={`mob-${n.testid}`}>
                  {n.label}
                </NavLink>
              ))}
              <div className="pt-3 border-t hairline">
                {user ? (
                  <button onClick={() => { logout(); setOpen(false); navigate("/"); }}
                    className="text-sm text-stone-700 flex items-center gap-1">
                    <LogOut size={14} /> Sign out ({user.email})
                  </button>
                ) : (
                  <div className="flex gap-4">
                    <Link to="/login" onClick={() => setOpen(false)} className="text-sm text-stone-700">Sign in</Link>
                    <Link to="/register" onClick={() => setOpen(false)} className="text-sm bg-[#9A3B3B] text-white px-4 py-2">Create account</Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-14">
        {children}
      </main>

      <footer className="border-t hairline mt-16 py-8">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-stone-500">
          <div className="font-serif-display italic">"Thy word is a lamp unto my feet, and a light unto my path."</div>
          <div>&copy; {new Date().getFullYear()} Logos Study &middot; Scripture from public-domain KJV, WEB, ASV via bolls.life</div>
        </div>
      </footer>
    </div>
  );
}
